import { z } from 'zod';
import { awaitedCommandSchema } from './commandSeat.schema';
import { objectIdSchema } from './common.schema';
import { calendarDateSchema } from './arrangement.schema';
import { FILL_STATES } from '../constants/fillState.constant';
import { ARRANGEMENT_TYPE_KEYS } from '../constants/arrangementType.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C1g §1] NOTIFICATIONS — A VIEW OVER OUTSTANDING WORK, NOT AN INBOX.
 *
 *  ⚠️ THERE IS NO STORED `addressed` FLAG, NO `read_at` ON AN ENTRY, AND NO NOTIFICATION
 *  COLLECTION. Confirmed with the client: only ACTING clears one. An entry exists because a
 *  fill inside the caller's scope is not complete, and it disappears when somebody names the
 *  last officer — which is the same shape [LIFECYCLE] gave completion and the same reason
 *  FR-5.3.6a forbids a stored completion flag. A boolean would need a writer on every
 *  assignment, removal, swap, re-stamp and delegation, and the first one to forget would make
 *  the bell say an unstaffed duty was done.
 *
 *  ⚠️ WHICH IS WHY THE COUNT AND THE NOVELTY ARE TWO DIFFERENT THINGS, AND BOTH ARE HERE.
 *
 *    `total`     — OUTSTANDING WORK. Unit-wide, derived, cleared only by doing it. One
 *                  nodal reading must not close another nodal's work.
 *    `isNew` /   — PER OFFICER. `officers.notifications_seen_at`, set when they last opened
 *    `newCount`    the bell. Without it a nodal with forty week-old items sees forty for
 *                  ever and stops looking, which is a bell nobody opens.
 *
 *  ⚠️ AND PERSONAL NOTIFICATIONS ARE NOT BUILT (§1h). The client says they exist and they
 *  are a DIFFERENT mechanism — stored, per officer, cleared by READING. Nothing produces one
 *  yet: cancellations reach OFFICERS through `domain_events`, which nothing drains, and the
 *  officer-facing half is explicitly later. Building the mechanism now would be speculative
 *  and would widen `outboxDeferred.test.ts`'s exact file list for a consumer that does not
 *  exist. Recorded in the CHANGELOG as a deliberate non-build.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ HOW MANY ORDERS THE POPOVER AND THE TILE RENDER — never how many are COUNTED.
 *
 * `total` is exact whatever this is (the aggregation's `$facet` counts after the group), so
 * a bell reading "12" above eight rows is honest and says so. A popover rendering every
 * order a commissionerate has open is the thing §1c exists to prevent.
 */
export const NOTIFICATION_ENTRY_LIMIT = 8;

/**
 * ⚠️ THE BELL RUNS FROM THE APP CHROME, SO ITS DEADLINE IS SHORTER THAN A TILE'S.
 *
 * `DASHBOARD_TILE_BUDGET_MS` is 4 000 and buys a tile on a screen somebody opened to read
 * numbers; this fires on every dashboard page, and a chrome element that can hold a request
 * open for four seconds is a page-load cost rather than a screen-load one. It is a real
 * cancellation (`maxTimeMS`) and not a `Promise.race` — [P6a]'s rule: a race settles the
 * promise and leaves the aggregation holding a pool connection.
 */
export const NOTIFICATION_BUDGET_MS = 2_000;

/**
 * ⚠️ [C1g §1c] ONE ENTRY PER **ARRANGEMENT**, WHICH IS NOT THE INBOX'S GRAIN.
 *
 * `inboxRowSchema` is division × duty point × night — FR-5.3.6b's grain, and the right one
 * for a screen somebody works through. A bell is not that screen: "3 orders need naming" is
 * something an operator opens and "247 shares" is something they turn off.
 */
export const notificationEntrySchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  /** Derived through `arrangementDisplayTitle`, so the bell and the list agree. */
  displayTitle: z.string(),
  paDate: calendarDateSchema,
  /** OPERATIONAL dates ([D4]), because the entry is about duty rather than about the order. */
  firstDutyDate: calendarDateSchema,
  lastDutyDate: calendarDateSchema,
  /** ⚠️ DUTIES, not shares. A duty addressed to three units is one duty (§1d's sentence). */
  dutyCount: z.number().int().min(0),
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /**
   * ⚠️ THE SAME `rank` THE PIPELINE FILTERED ON, decoded — never a second derivation here, or
   * the entry could be listed under one answer and coloured with another.
   *
   * ⚠️ [amended C6a] `COMPLETE` IS REACHABLE NOW, AND ONLY ONE WAY: the force is finished (every
   * share settled, or declared short) while a command role asked of a division is still awaited.
   * That is why `closedShort` is on this entry again — [C5b §3] withheld it only because COMPLETE
   * could not arrive here — and why the renderer states no force word when the force is COMPLETE.
   */
  fillState: z.enum(FILL_STATES),
  /** ⚠️ [C6a] The force group's own `CLOSED_SHORT_EXPRESSION`, so a COMPLETE force reads its true word. */
  closedShort: z.boolean(),
  /**
   * ⚠️ [C6a] THE COMMAND ROLES STILL AWAITED ON THIS ORDER, AS WORDS — `In-charge` from `CD`.
   *
   * ONE entry per order still ([C1g §1c]): a division owing both force and an In-charge reads
   * `3 of 12 named · In-charge awaited from CD`, never two entries. `[]` when no command role is
   * outstanding for this caller. LABELS, never a number: nothing here counts a command role, and
   * the force figures beside it are unchanged by it (`commandSeats.test.ts` asserts both).
   */
  commandAwaited: z.array(awaitedCommandSchema),
  /**
   * ⚠️ [C6a] WHERE A COMMAND-ONLY ENTRY LEADS. The Assign PA inbox lists force shares only, so an
   * entry whose only open work is a command role links to the nearest duty still open for naming on
   * which it is awaited. `null` when no command role is awaited, or none is still nameable.
   */
  commandDutyId: objectIdSchema.nullable(),
  /**
   * ⚠️ PER OFFICER, AND IT IS THE ONLY FIELD ON THIS ROW THAT IS. Derived by comparing the
   * newest share's write instant with `officers.notifications_seen_at`.
   */
  isNew: z.boolean(),
  /**
   * ⚠️ [C1e §2f] NORMALLY TRUE, AND FALSE AFTER A REPARENT. A share's closure and its order's
   * are written by two functions, and `reparentOrgUnit` resyncs NEITHER — so a nodal of the
   * new parent can hold the share and get a 404 on the order. `inboxRowSchema` carries the
   * same flag for the same reason; an entry that cannot be opened must not offer a link.
   */
  arrangementVisible: z.boolean(),
});

export const notificationsSchema = z.object({
  /** ⚠️ EXACT, and independent of `NOTIFICATION_ENTRY_LIMIT`. */
  total: z.number().int().min(0),
  /** How many of `total` arrived after this officer last opened the bell. */
  newCount: z.number().int().min(0),
  /**
   * ⚠️ [C6a S2] HOW MANY OF `total` HAVE **FORCE** WORK OUTSTANDING — the orders the Assign PA inbox
   * actually lists. An order whose only open work is a command role asked of a division is in
   * `total` (it is outstanding) and NOT here (the inbox lists force shares alone), so the popover's
   * "N more in Assign PA" is taken from this and promises nothing the inbox will not show. Counted in
   * the same `$facet` as `total`, so the two cannot disagree about which orders exist. An ORDER
   * count of force work — never a figure about a command role (FR-5.2.6b).
   */
  forceTotal: z.number().int().min(0),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7B §5d] TYPED ORDERS, FOLDED — ONE ROW PER TYPE, NEVER ONE ENTRY PER ORDER.
   *
   *  182 Durga Puja orders published in one evening would otherwise take every one of the eight
   *  entries and push the ordinary work off the bell. So typed orders are counted here instead of
   *  listed in `entries`, which then holds untyped orders only.
   *
   *  ⚠️ `total`, `newCount` AND `forceTotal` STAY INCLUSIVE — the exact number of orders with work
   *  left, typed ones among them — so none of them changes meaning. Each fold carries its own share
   *  of the three, and the popover's "N more in Assign PA" subtracts the folds' `forceTotal`.
   *
   *  ⚠️ OPTIONAL, OMITTED WHEN NO TYPED ORDER HAS WORK LEFT, so the payload is byte-identical to the
   *  one before P7B for a caller with no typed work. ORDER counts of force work — never a figure about
   *  a command role (FR-5.2.6b); `commandRoleIsolation.test.ts` CLAIM 6c pins these keys.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  typeGroups: z
    .array(
      z.object({
        type: z.enum(ARRANGEMENT_TYPE_KEYS),
        total: z.number().int().min(0),
        newCount: z.number().int().min(0),
        forceTotal: z.number().int().min(0),
      }),
    )
    .optional(),
  entries: z.array(notificationEntrySchema),
  /**
   * ⚠️ ECHOED, NEVER ASSUMED. The window is the server's (`INBOX_DEFAULT_WINDOW_DAYS`), so the
   * popover can say which days it covers rather than the client re-deriving "today" in a zone
   * it does not own — `dashboardQuerySchema`'s rule, and `istDutyDate` is the one crossing.
   */
  from: calendarDateSchema,
  to: calendarDateSchema,
  /** `null` until this officer has opened the bell once. */
  seenAt: z.iso.datetime().nullable(),
  generatedAt: z.iso.datetime(),
});

/**
 * ⚠️ NO BODY. The mark is ALWAYS "now, server time", because a client-supplied instant would
 * let a tab with a wrong clock mark everything read for ever — and there is no second thing a
 * caller could legitimately ask for. [D4.5]'s rule inverted: `now` is a parameter to the
 * SERVICE and the controller reads the clock.
 */
export const markNotificationsSeenSchema = z.object({
  seenAt: z.iso.datetime(),
});
